import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTargetScoreQuery } from "@/hooks/queries";
import { TARGET_SCORE_OPTIONS } from "@/lib/constant";
import { cn } from "@/lib/utils";

type ScoreModalProps = {
  open: boolean;
  score: number;
  isPassed: boolean;
  recallCount: number;
  onConfirm: () => void;
  onStartRecall: () => void;
  onSkipRecall: () => void;
};

export function ScoreModal({
  open,
  score,
  isPassed,
  recallCount,
  onConfirm,
  onStartRecall,
  onSkipRecall,
}: ScoreModalProps) {
  const { data: targetScore = TARGET_SCORE_OPTIONS[2] } = useTargetScoreQuery();

  return (
    <Dialog open={open}>
      <DialogContent showCloseButton={false} className="max-w-sm" style={{ boxShadow: "none" }}>
        {/* 테스트 결과 타이틀 */}
        <DialogHeader className="items-center gap-1.5 text-center">
          <DialogTitle className="text-2xl font-black text-foreground">오늘 학습 완료!</DialogTitle>
          <p className={cn("text-sm font-extrabold", isPassed ? "text-primary" : "text-secondary")}>
            {isPassed ? "TEST PASSED!" : "TEST FAILED!"}
          </p>
          <p className="text-sm text-muted-foreground">오늘 목표를 끝까지 마쳤어요.</p>
        </DialogHeader>

        {/* 점수 */}
        <DialogDescription
          className={cn(
            "rounded-lg p-8 flex flex-col items-center gap-1.5",
            isPassed ? "bg-primary-background" : "bg-secondary-background",
          )}
        >
          <span className={cn("text-4xl font-black", isPassed ? "text-primary" : "text-secondary")}>{score}점</span>
          <span className="text-sm text-muted-foreground">(목표 {targetScore}점)</span>
          {recallCount > 0 && (
            <span className="mt-3 text-center text-sm font-semibold text-foreground">
              틀린 단어 {recallCount}개를 바로 다시 떠올려 볼까요?
            </span>
          )}
        </DialogDescription>

        {/* 확인 버튼 */}
        <DialogFooter className="border-t-0 bg-transparent -mt-4">
          {recallCount > 0 ? (
            <div className="flex w-full flex-col gap-2">
              <Button
                variant="default"
                size="lg"
                onClick={onStartRecall}
                className="w-full rounded-md py-6 text-base font-bold"
              >
                짧게 다시 풀기
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={onSkipRecall}
                className="w-full rounded-md text-muted-foreground"
              >
                건너뛰고 리포트 보기
              </Button>
            </div>
          ) : (
            <Button
              variant="default"
              size="lg"
              onClick={onConfirm}
              className={cn(
                "w-full rounded-md py-6 text-base font-bold text-white transition-colors",
                isPassed ? "bg-primary hover:bg-primary/80" : "bg-secondary hover:bg-secondary/80",
              )}
            >
              확인
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
